import React, { useEffect, useRef, useState } from 'react';

import { buscarClientes } from '../../services/clienteService';
import { apellidoNombre } from '../../utils/nombrePersona';

/** Milisegundos de espera antes de pegarle al backend. Mismo valor que el resto de los
 *  buscadores predictivos del sistema, para que la sensación al tipear sea la misma. */
const DEBOUNCE_MS = 350;

/** Más de esto no entra en pantalla sin scrollear, y si hacen falta más el criterio es malo. */
const MAX_RESULTADOS = 6;

/**
 * Buscador predictivo de clientes por CUIL, nombre o apellido.
 *
 * Es la extracción del bloque que vivía dentro de ModalCrearTurno (TurneroPage). Se sacó afuera
 * cuando VincularClienteTurno necesitó exactamente lo mismo: eran el mismo widget: mismo debounce,
 * mismo tope de resultados, mismo markup de list-group.
 *
 * Devuelve el cliente elegido por onSeleccionar y nada más — quién lo usa decide qué hacer con
 * él (prellenar un formulario, vincular un turno, etc.).
 */
export default function BuscadorClienteInline({
    etiqueta = '¿Ya es cliente del estudio?',
    placeholder = 'Buscar por CUIL, nombre o apellido...',
    onSeleccionar,
    autoFocus = false,
    claseEtiqueta = '',
}) {
    const [busqueda, setBusqueda] = useState('');
    const [encontrados, setEncontrados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const debounce = useRef(null);

    useEffect(() => {
        const criterio = busqueda.trim();
        if (debounce.current) clearTimeout(debounce.current);

        if (!criterio) {
            setEncontrados([]);
            setBuscando(false);
            return undefined;
        }

        setBuscando(true);
        debounce.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(criterio);
                setEncontrados((resultados || []).slice(0, MAX_RESULTADOS));
            } catch (e) {
                setEncontrados([]);
            } finally {
                setBuscando(false);
            }
        }, DEBOUNCE_MS);

        return () => { if (debounce.current) clearTimeout(debounce.current); };
    }, [busqueda]);

    const elegir = (cliente) => {
        setBusqueda('');
        setEncontrados([]);
        if (onSeleccionar) onSeleccionar(cliente);
    };

    return (
        <>
            <label className={`form-label fw-semibold ${claseEtiqueta}`}>
                <i className="bi bi-search me-1" />{etiqueta}
            </label>
            <input
                className="form-control"
                placeholder={placeholder}
                value={busqueda}
                autoComplete="off"
                autoFocus={autoFocus}
                onChange={(e) => setBusqueda(e.target.value)}
            />
            {buscando && <small className="text-muted d-block mt-1">Buscando...</small>}
            {!buscando && busqueda.trim() && encontrados.length === 0 && (
                <small className="text-muted d-block mt-1">Sin resultados.</small>
            )}
            {encontrados.length > 0 && (
                <ul className="list-group mt-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {encontrados.map((cliente) => (
                        <li
                            key={cliente.cuil}
                            className="list-group-item list-group-item-action"
                            role="button"
                            onClick={() => elegir(cliente)}
                        >
                            {apellidoNombre(cliente.apellido, cliente.nombre)}
                            <span className="text-muted"> · {cliente.cuil}</span>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}
